import { appSelector } from '../../../../shared/store/slices/app.slice';
import { pause, play, retry } from '@shared/store/slices/media.slice';
import { routerSelector } from '@shared/store/slices/router.slice';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useAppDispatch, useAppSelector } from '../../../../shared/store/hooks';
import AcceptButton from '../../atoms/buttons/genericButton';
import DeviceConfig from '@devices/config';
import NetworkScreenContainer from '../../atoms/container/networkScreenContainer';
import NetworkImage from '../../atoms/images/networkImage';
import NetworkTitle from '../../atoms/text/networkTitle';
import NetworkDescription from '../../atoms/text/networkDescription';
import NetworkButtonContainer from '../../atoms/container/networkButtonContainer';
import Pages from '@pages';
import Spinner from '@components/elements/spinner/spinner';
import TranslationManager from '../../../../shared/assets/locale/translationsManager';
import useKeyboard from '../../../../shared/components/utils/hooks/useKeyboard';

const CHECK_TIMEOUT = 2000;

function Network() {
  const dispatch = useAppDispatch();
  const { status } = useAppSelector(appSelector);
  const { route } = useAppSelector(routerSelector);
  const isDisconnected = useRef<boolean>();
  const [checking, setChecking] = useState(false);

  // Event handlers

  const onConnect = useCallback(() => {
    isDisconnected.current = false;
    switch (route.Page) {
      case Pages.PLAYER:
        dispatch(play());
        break;
      case Pages.ZAPPER:
        dispatch(retry());
        break;
      default:
        location.reload();
    }
  }, [route]);

  const onDisconnect = useCallback(() => {
    isDisconnected.current = true;
    switch (route.Page) {
      case Pages.PLAYER:
      case Pages.ZAPPER:
        dispatch(pause());
        break;
    }
  }, [route]);

  // Effect hooks

  useEffect(() => {
    if (!status.network) {
      onDisconnect();
    } else if (isDisconnected.current) {
      onConnect();
    }
  }, [status]);

  useKeyboard({
    canBeFocused: false,
    keyDownHandler: (key) => {
      if (key === 'SELECT') {
        // Checking to true will display a spinner to provide feedback to the user
        setChecking(true);
        if (isDisconnected.current && DeviceConfig.checkNetwork()) {
          onConnect();
        }
        setTimeout(() => {
          setChecking(false);
        }, CHECK_TIMEOUT);
      }
    },
  });

  if (!status.network) {
    const img =
      'data:image/png;base64,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';
    return (
      <NetworkScreenContainer>
        <NetworkImage src={img}></NetworkImage>
        <NetworkTitle>
          {TranslationManager.get('COMPONENTS.NETWORK.TITLE')}
        </NetworkTitle>
        <NetworkDescription>
          {TranslationManager.get('COMPONENTS.NETWORK.DESCRIPTION')}
        </NetworkDescription>
        <NetworkButtonContainer>
          {checking ? (
            <Spinner type='network' />
          ) : (
            <AcceptButton onSelect={() => setChecking(true)} focused={true}>
              OK
            </AcceptButton>
          )}
        </NetworkButtonContainer>
      </NetworkScreenContainer>
    );
  }
  return null;
}

export default Network;
